import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Plus, Building, Package, Wrench, Scale } from 'lucide-react';
import { Button, TableHeader, CardStats, ConfirmModal, SearchInput, Pagination, ExportDropdown } from '@components/ui';
import { TablePageSkeleton } from '@components/skeleton';
import { usePagination, useExportExcel } from '@hooks';
import { getCompaniesByConstructionId, getCompanyStats } from '@mocks/firmalar';
import { getCariStats } from '@mocks/cariHesaplar';
import FirmalarTablosu from './FirmalarTablosu';
import FirmaModal from './FirmaModal';


export default function Companies() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Export hook
    const { exportToExcel, isExporting } = useExportExcel();

    const allCompanies = getCompaniesByConstructionId(constructionId);
    const stats = getCompanyStats(constructionId);
    const cariStats = getCariStats(constructionId);

    // Filter companies based on search query
    const companies = allCompanies.filter(company => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            company.name?.toLowerCase().includes(query) ||
            company.type?.toLowerCase().includes(query) ||
            company.contact?.toLowerCase().includes(query)
        );
    });

    // Pagination
    const {
        paginatedData,
        currentPage,
        totalPages,
        totalItems,
        pageSize,
        goToPage,
        resetPage,
    } = usePagination(companies, 10);

    // Reset pagination when search changes
    useEffect(() => {
        resetPage();
    }, [searchQuery]);


    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const handleAdd = () => {
        setModalOpen(true);
    };

    const handleDelete = (company) => {
        setSelectedCompany(company);
        setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        console.log('Firma silindi:', selectedCompany);
        toast.success(`${selectedCompany?.name} başarıyla silindi`);
        setDeleteModalOpen(false);
        setSelectedCompany(null);
    };

    const handleSubmit = (data) => {
        console.log('Firma eklendi:', data);
        toast.success(`${data.name} başarıyla eklendi`);
    };

    // Loading state - API entegrasyonunda true olarak başlatılacak
    const [isLoading] = useState(false);
    if (isLoading) return <TablePageSkeleton statsCount={4} tableRows={10} tableColumns={7} />;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Stats */}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CardStats
                    title="Toplam Firma"
                    value={stats.total}
                    icon={Building}
                    color="primary"
                    size="sm"
                />
                <CardStats
                    title="Taşeronlar"
                    value={stats.subcontractors}
                    icon={Wrench}
                    color="info"
                    size="sm"
                />
                <CardStats
                    title="Tedarikçiler"
                    value={stats.suppliers}
                    icon={Package}
                    color="secondary"
                    size="sm"
                />
                <CardStats
                    title="Açık Cari Bakiye"
                    value={formatCurrency(cariStats.totalBalance)}
                    icon={Scale}
                    color={cariStats.totalBalance > 0 ? 'error' : 'success'}
                    size="sm"
                />
            </div>

            {/* Table Header */}
            <TableHeader
                title="Firmalar"
                subtitle={showAllConstructions ? 'Tüm inşaatlar' : selectedConstruction?.name}
            >
                <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Firma adı, tür veya yetkili ara..."
                    size="md"
                />
                <ExportDropdown
                    onExportExcel={() => exportToExcel({
                        data: companies,
                        columns: [
                            { header: 'Firma Adı', key: 'name' },
                            { header: 'Tür', key: 'type' },
                            { header: 'Yetkili', key: 'contact' },
                            { header: 'Telefon', key: 'phone' },
                            { header: 'Email', key: 'email' },
                        ],
                        fileName: 'firmalar_listesi',
                        sheetName: 'Firmalar',
                    })}
                    isExporting={isExporting}
                    disabled={companies.length === 0}
                />
                <Button

                    variant="primary"
                    icon={<Plus size={18} />}
                    onClick={handleAdd}
                >
                    Firma Ekle
                </Button>
            </TableHeader>

            {/* Table */}
            <FirmalarTablosu data={paginatedData} onDelete={handleDelete} />

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={goToPage}
            />


            {/* Add Modal */}
            <FirmaModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
            />

            {/* Delete Confirm Modal */}
            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Firmayı Sil"
                message={`"${selectedCompany?.name}" firmasını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
                confirmText="Evet, Sil"
                variant="danger"
            />
        </div>
    );
}
